<template>
  <b-modal
    ref="editPatientModal"
    centered
    size="xxl"
    modal-class="edit-patient-modal"
    :title="$t('global.editPatient')"
    qa="edit-patient-modal"
    @hide="modalShown = false"
    @hidden="$_cleanComponentData()"
    @shown="modalShown = true"
  >
    <patient-form
      v-if="modalShown"
      ref="patientForm"
      :patient="patient"
      :notes="notes"
    />
    <div
      slot="modal-footer"
      class="footer-banner w-100"
    >
      <div
        class="row"
      >
        <div class="col-6 text-left">
          <b-button
            qa="edit-patient-modal-cancel-button"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <vt-loading-button
            qa="edit-patient-modal-save-button"
            class="btn--primary btn-sm"
            :params="{ promise: edit }"
          >
            <i
              class="far fa-save mr-2"
              aria-hidden="true"
            />{{ $t('global.save') }}
          </vt-loading-button>
        </div>
      </div>
    </div>
    <b-modal
      ref="errorModal"
      hide-footer
      centered
    >
      <p class="my-4">
        {{ $t('global.operationNotAllowed') }}
      </p>
    </b-modal>
  </b-modal>
</template>

<script>

import patientResource from '@/api/resources/PatientResource'
import PatientForm from '@/components/patient/PatientForm'
import patientNotesService from '@/api/resources/clob/PatientNotesService'
import { cloneDeep, isEqual } from 'lodash'
import PatientService from '@/api/resources/PatientService'

export default {
    name: 'EditPatientModal',
    components: {
        PatientForm
    },
    data() {
        return {
            modalShown: false,
            errRes: null,
            patId: null,
            patient: {},
            notes: {},
            originalNotes: {},
            provinces: [],
            municipalities: []
        }
    },
    computed: {
        okDisabled() {
            return false // TODO: validate that every required input is filled
        }
    },
    methods: {
        async show(patId) {
            this.patId = patId
            await this.loadPatient()
        },
        hide() {
            this.$refs.editPatientModal.hide()
        },
        async edit() {
            const valid = await this.$refs.patientForm.validate()
            if (!valid) {
                return
            }
            const successOnUpdatingPatient = await this.editPatient()
            if (successOnUpdatingPatient === false) {
                return
            }
            const successOnUpdatingNotes = await this.editNotes()
            if (successOnUpdatingNotes) {
                this.showToastSuccessGlobal()
                this.$refs.editPatientModal.hide()
            }
        },
        async editPatient() {
            if (!this.hasPatientBeenChanged()) {
                return true
            }
            const requestPatient = {
                ...PatientService.mapFromResponseToRequest(this.$refs.patientForm.internalPatient),
                patientIdentifiers: this.$refs.patientForm.toPatientIdentifierRequest()
            }
            const [err, response] = await this.toCall(patientResource.edit(this.patId, requestPatient))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$t('global.patient'), err, () => {
                        this.patient.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.editPatient()
                    })
                } else if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.BAD_REQUEST) {
                    this.showErrorModalGlobal(err.response.data.message)
                    return false
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            }
            this.$eventHub.$emit('updatePatientInformation', response)
            this.$emit('edited', response)
            return true
        },
        async editNotes() {
            if (!this.hasNotesBeenChanged()) {
                return true
            }
            const [err] = await this.toCall(patientNotesService.update(this.patId, this.notes))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$t('global.patient'), err, async () => {
                        this.notes.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.editNotes()
                    })
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            }
            return true
        },
        async loadPatient() {
            const [err, response] = await this.toCall(patientResource.findById(this.patId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const patient = response
            for (const identifier of patient.patientIdentifiers) {
                identifier.center && (identifier.centerId = identifier.center.id)
                identifier.identifierTypeId = identifier.identifierType.idtId
                delete identifier.center
                delete identifier.identifierType
            }
            this.notes = patient.notes
            this.originalNotes = cloneDeep(this.notes)
            delete patient.notes
            this.patient = cloneDeep(patient)
            this.$refs.editPatientModal.show()
        },
        hasPatientBeenChanged() {
            const patientToCompare = cloneDeep(this.$refs.patientForm.internalPatient)
            patientToCompare.patientIdentifiers.forEach(identifier => delete identifier.internalId)
            return !isEqual(this.patient, patientToCompare)
        },
        hasNotesBeenChanged() {
            return !isEqual(this.originalNotes, this.notes)
        }
    }
}

</script>
